import clsx from "clsx";
import { CSSProperties, FC } from "react";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { PillButton, PillButtonProps } from "mds/components/PillButton";
import { ChevronUpIcon, PresentPlayBackgroundIcon, PresentPlayIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectIsClassSessionInPerson, selectIsDesktopDevice } from "store/selectors";
import { selectCurrentBreakoutGroupId } from "store/selectors/breakouts";
import { useAv } from "utils/av";

const LazyScreenshareMenuOption = withActiveAv(() => import("components/av/ScreenshareMenuOption"));

export type PagePresentingMenuProps = {
  styles?: CSSProperties;
  onPractice?: () => void;
  onPresent?: () => void;
  size?: PillButtonProps["size"];
  isDisabled?: boolean;
  isPractice?: boolean;
};

export const PagePresentingMenu: FC<PagePresentingMenuProps> = ({
  styles,
  onPractice,
  onPresent,
  size,
  isDisabled,
  isPractice,
}) => {
  const isInPersonClassSession = useAppSelector(selectIsClassSessionInPerson);
  const { isInVirtualClass } = useAv();
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);
  const isDesktopDevice = useAppSelector(selectIsDesktopDevice);

  if (isPractice) {
    return (
      <PillButton
        className={clsx(
          "gap-2 border-0 font-medium",
          isDisabled ? "text-black-tint-40" : "text-white",
        )}
        disabled={isDisabled}
        kind="gray"
        size={size}
        onClick={onPractice}
      >
        <PresentPlayIcon />
        {t("page_presenting_menu.practice")}
      </PillButton>
    );
  }

  if (isInPersonClassSession) {
    return (
      <PillButton
        className={clsx(
          "gap-2 border-0 font-medium",
          isDisabled ? "text-black-tint-40" : "text-white",
        )}
        disabled={isDisabled}
        kind="gray"
        size={size}
        onClick={onPresent}
      >
        <PresentPlayIcon />
        {t("page_presenting_menu.present")}
      </PillButton>
    );
  }

  const MenuButton = (
    <PillButton
      className={clsx(
        "gap-2 border-0 font-medium",
        isDisabled ? "text-black-tint-40" : "text-white",
      )}
      disabled={isDisabled}
      kind="gray"
      size={size}
    >
      <PresentPlayIcon />
      {t("page_presenting_menu.present")}
      <ChevronUpIcon />
    </PillButton>
  );

  // If the user is not in desktop and in a breakout group, there will be no available options
  if (currentBreakoutGroupId && !isDesktopDevice) return null;

  return (
    <Menu
      className="dark-menu min-w-[180px]"
      direction="top"
      menuButton={MenuButton}
      menuStyle={styles}
    >
      {!currentBreakoutGroupId && (
        <MenuItem eventName="Presentation Menu - Start" onClick={onPresent}>
          <IconText
            iconStart={<PresentPlayBackgroundIcon className="text-green" />}
            text={t("page_presenting_menu.start_presenting")}
          />
        </MenuItem>
      )}

      {isDesktopDevice && isInVirtualClass && !currentBreakoutGroupId && <MenuDivider />}

      {isDesktopDevice && <LazyScreenshareMenuOption />}
    </Menu>
  );
};
